import { z } from 'zod';
import { profileSummarySchema } from './game-profile.js';
import { daemonSyncSchema } from './infra.js';

/** One top-level file or folder name (no slashes). */
const topLevelName = z
  .string()
  .min(1)
  .max(255)
  .refine((n) => !n.includes('/') && !n.includes('\\') && n !== '.' && n !== '..', {
    message: 'A name in the server folder, without slashes.',
  });

/** Fix-ups the importer can apply itself; anything else is explained, not done. */
export const importFixSchema = z.discriminatedUnion('kind', [
  /** Point the egg's jar variable at a jar that is already in the server folder. */
  z.object({ kind: z.literal('use-jar'), file: topLevelName }),
  /** Move everything inside one folder up to the top of the server folder. */
  z.object({ kind: z.literal('move-up'), folder: topLevelName }),
]);
export type ImportFix = z.infer<typeof importFixSchema>;

export const importCheckResultSchema = z.object({
  /** Stable id, e.g. "server-jar" or "file:/server.properties". */
  id: z.string(),
  label: z.string(),
  /** fail blocks finishing; warn is worth knowing but the server can still run. */
  status: z.enum(['pass', 'warn', 'fail']),
  /** What was found. */
  detail: z.string(),
  /** What to do about it, when anything. */
  action: z.string().nullable(),
  /** A fix-up the importer can do, when there is one. */
  fix: importFixSchema.nullable(),
});
export type ImportCheckResult = z.infer<typeof importCheckResultSchema>;

export const importReportSchema = z.object({
  profile: profileSummarySchema,
  /** No check failed. */
  ready: z.boolean(),
  checks: z.array(importCheckResultSchema),
});
export type ImportReport = z.infer<typeof importReportSchema>;

export const importFinishSchema = z
  .object({
    /** False when a check failed; nothing was recorded. */
    finished: z.boolean(),
    report: importReportSchema,
  })
  .extend(daemonSyncSchema.shape);
export type ImportFinish = z.infer<typeof importFinishSchema>;
